import {
  focusManager,
  onlineManager,
  type Query,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { connectivityStore } from '../connectivity-store.js';
import { sdkEvents } from '../events.js';
import { hasActiveFetchOnceRealtimeOwner } from '../internal/fetch-once-realtime-policy.js';
import { getCachedQuery } from '../internal/get-cached-query.js';
import { refetchRealtimeQuery } from '../internal/refetchRealtimeQuery.js';
import {
  OUTAGE_POLL_INTERVALS_MS,
  type PollingPolicy,
  pollingDelay,
  resolvePollingInterval,
} from './policy.js';
import { pollingBlocked, pollingState, subscribePollingState } from './state.js';

export interface PollingOwner {
  readonly policy: PollingPolicy;
  readonly operationName: string;
  readonly active: () => boolean;
  readonly batched: boolean;
}

export interface PollingDiagnostic {
  readonly operationName: string;
  readonly mode: 'paused' | 'polling' | 'recovering' | 'cooldown';
  readonly intervalMs: number | false;
  readonly lastRestAt: number;
  readonly nextRefreshAt: number | null;
  readonly cooldownUntil: number;
}

interface Entry {
  readonly query: Query;
  readonly owners: Set<PollingOwner>;
  timer?: ReturnType<typeof setTimeout> | undefined;
  due: number | null;
  interval: number | false;
  signature: string;
  lastRestAt: number;
  catchup: boolean;
}

const coordinators = new WeakMap<QueryClient, PollingCoordinator>();

export function getPollingSnapshot(client: QueryClient): readonly PollingDiagnostic[] {
  return coordinators.get(client)?.snapshot() ?? [];
}

export function getPollingCoordinator(client: QueryClient): PollingCoordinator {
  let coordinator = coordinators.get(client);
  if (coordinator === undefined) {
    coordinator = new PollingCoordinator(client);
    coordinators.set(client, coordinator);
  }
  return coordinator;
}

/** One scheduler per query, even when data owners mount at different times. */
export class PollingCoordinator {
  private readonly entries = new Map<Query, Entry>();
  private teardowns: (() => void)[] = [];

  constructor(
    private readonly client: QueryClient,
    private readonly random: () => number = Math.random,
  ) {}

  register(key: QueryKey, owner: PollingOwner): () => void {
    const query = getCachedQuery(this.client, { queryKey: key });
    if (query === undefined) return () => {};
    if (this.entries.size === 0) this.install();
    let entry = this.entries.get(query);
    if (entry === undefined) {
      entry = {
        query,
        owners: new Set(),
        due: null,
        interval: false,
        signature: '',
        lastRestAt: 0,
        catchup: false,
      };
      this.entries.set(query, entry);
    }
    entry.owners.add(owner);
    this.schedule(entry);
    return () => {
      entry.owners.delete(owner);
      if (entry.owners.size === 0) {
        this.clear(entry);
        this.entries.delete(query);
      }
      this.stopIfEmpty();
      if (entry.owners.size > 0) this.schedule(entry);
    };
  }

  refresh = (): void => {
    for (const entry of this.entries.values()) this.schedule(entry);
  };

  refreshQuery(key: QueryKey): void {
    const query = getCachedQuery(this.client, { queryKey: key });
    if (query === undefined) return;
    const entry = this.entries.get(query);
    if (entry !== undefined) this.schedule(entry);
  }

  snapshot(): readonly PollingDiagnostic[] {
    return [...this.entries.values()].map((entry) => this.diagnostic(entry));
  }

  private stopIfEmpty(): void {
    if (this.entries.size !== 0) return;
    for (const stop of this.teardowns) stop();
    this.teardowns = [];
  }

  private install(): void {
    this.teardowns = [
      focusManager.subscribe(this.refresh),
      onlineManager.subscribe(this.refresh),
      subscribePollingState(this.client, (query) => {
        const entry = this.entries.get(query);
        if (entry !== undefined) this.schedule(entry);
      }),
      this.client.getQueryCache().subscribe((event) => {
        if (event.type === 'removed') {
          const entry = this.entries.get(event.query);
          if (entry !== undefined) this.clear(entry);
          this.entries.delete(event.query);
          this.stopIfEmpty();
        } else if (event.type === 'updated') {
          const entry = this.entries.get(event.query);
          if (entry !== undefined) this.schedule(entry);
        }
      }),
      connectivityStore.subscribe((next, previous) => {
        if (next.state === previous.state && next.lastReconnectAt === previous.lastReconnectAt)
          return;
        for (const entry of this.entries.values()) {
          if ([...entry.owners].some((owner) => owner.policy.hasNats)) {
            const state = pollingState(entry.query);
            state.recoveryEpoch += 1;
            state.recovering = true;
            state.wireReady = false;
            if (previous.state === 'CONNECTED') state.adaptiveStep = 0;
            if (next.state === 'CONNECTED') entry.catchup = true;
          }
        }
        this.refresh();
      }),
    ];
  }

  private schedule(entry: Entry): void {
    const { query } = entry;
    const state = pollingState(query);
    const connectivity = connectivityStore.getState().state;
    const owners = [...entry.owners].filter((owner) => owner.active());
    let interval: number | false = false;
    let explicit = true;
    if (
      query.isActive() &&
      focusManager.isFocused() &&
      onlineManager.isOnline() &&
      !pollingBlocked(query) &&
      !hasActiveFetchOnceRealtimeOwner(this.client, query.queryKey)
    ) {
      for (const owner of owners) {
        const candidate = resolvePollingInterval(
          owner.policy,
          connectivity,
          state.recovering,
          OUTAGE_POLL_INTERVALS_MS[state.adaptiveStep] ?? 4_000,
        );
        if (candidate !== false && (interval === false || candidate < interval)) {
          interval = candidate;
          explicit = owner.policy.explicit === true;
        }
      }
    }
    if (interval === false) {
      this.clear(entry);
      entry.interval = false;
      this.report(entry);
      return;
    }
    const changed = entry.interval !== interval;
    const previousDue = entry.due;
    entry.interval = interval;
    if (entry.due === null || changed || entry.lastRestAt !== state.lastRestAt) {
      entry.due = (state.lastRestAt || Date.now()) + pollingDelay(interval, explicit, this.random);
      entry.lastRestAt = state.lastRestAt;
    }
    if (entry.catchup) entry.due = Date.now();
    entry.due = Math.max(entry.due, state.cooldownUntil);
    if (query.state.fetchStatus !== 'idle') {
      this.clearTimer(entry);
      this.report(entry);
      return;
    }
    if (previousDue !== entry.due) this.clearTimer(entry);
    if (entry.timer === undefined) {
      entry.timer = setTimeout(
        () => {
          entry.timer = undefined;
          void this.tick(entry);
        },
        Math.min(2_147_483_647, Math.max(0, entry.due - Date.now())),
      );
    }
    this.report(entry);
  }

  private async tick(entry: Entry): Promise<void> {
    if (!this.entries.has(entry.query)) return;
    const state = pollingState(entry.query);
    if (
      entry.interval === false ||
      !focusManager.isFocused() ||
      !onlineManager.isOnline() ||
      ![...entry.owners].some((owner) => owner.active())
    ) {
      this.refresh();
      return;
    }
    if (
      state.cooldownUntil > Date.now() ||
      (entry.due !== null && entry.due > Date.now()) ||
      entry.query.state.fetchStatus !== 'idle'
    ) {
      this.refresh();
      return;
    }
    entry.catchup = false;
    state.batched = [...entry.owners].some((owner) => owner.active() && owner.batched);
    // Reserve the next deadline before refetch synchronously notifies observers.
    entry.due = Date.now() + entry.interval;
    try {
      await refetchRealtimeQuery(this.client, entry.query.queryKey);
    } finally {
      state.batched = false;
      if (this.entries.has(entry.query)) {
        this.clearTimer(entry);
        this.schedule(entry);
      }
    }
  }

  private clearTimer(entry: Entry): void {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    entry.timer = undefined;
  }
  private clear(entry: Entry): void {
    this.clearTimer(entry);
    entry.due = null;
  }

  private diagnostic(entry: Entry): PollingDiagnostic {
    const state = pollingState(entry.query);
    return {
      operationName: [...entry.owners][0]?.operationName ?? '',
      mode:
        entry.interval === false
          ? 'paused'
          : state.cooldownUntil > Date.now()
            ? 'cooldown'
            : state.recovering
              ? 'recovering'
              : 'polling',
      intervalMs: entry.interval,
      lastRestAt: state.lastRestAt,
      nextRefreshAt: entry.due,
      cooldownUntil: state.cooldownUntil,
    };
  }

  private report(entry: Entry): void {
    const diagnostic = this.diagnostic(entry);
    const signature = `${diagnostic.mode}:${diagnostic.intervalMs}`;
    if (entry.signature === signature) return;
    entry.signature = signature;
    sdkEvents.emit('polling:policy', {
      operationName: diagnostic.operationName,
      mode: diagnostic.mode,
      intervalMs: diagnostic.intervalMs,
      reason: connectivityStore.getState().state,
    });
  }
}
